/**
 * @fileoverview One-time migration for time-control-specific ratings
 *
 * Migrates users from legacy single-rating system to time-control-specific ratings.
 * Run automatically on user login if timeControlRatings is empty/undefined.
 */

import { UserProfile } from './types';
import { UserDataService } from '../firebase/userDataService';
import { MMRService } from './MMRService';

/**
 * One-time migration: Copy legacy ratings to appropriate time control
 * Run on user login if timeControlRatings is empty/undefined
 */
export async function migrateUserToTimeControlRatings(
  userId: string,
  profile: UserProfile
): Promise<UserProfile> {
  // Check if already migrated
  if ((profile.mmr.timeControlRatings?.standard?.games ?? 0) > 0 ||
      (profile.mmr.timeControlRatings?.marathon?.games ?? 0) > 0) {
    return profile; // Already migrated
  }


  if (!profile.mmr.timeControlRatings) {
    profile.mmr.timeControlRatings = {};
  }

  if (profile.mmr.isPlaced) {
    // User has completed placement - copy to Standard
    profile.mmr.timeControlRatings.standard = {
      rating: profile.mmr.displayRating,
      games: profile.mmr.rankedGames,
      peak: profile.mmr.peak,
      isPlaced: true,
      placementGames: 10,
      provisionalRating: profile.mmr.displayRating
    };
  } else {
    // User is in placement - copy to Marathon (finish there)
    profile.mmr.timeControlRatings.marathon = {
      rating: profile.mmr.provisionalRating,
      games: profile.mmr.placementGames,
      peak: profile.mmr.provisionalRating,
      isPlaced: false,
      placementGames: profile.mmr.placementGames,
      provisionalRating: profile.mmr.provisionalRating
    };
  }

  await UserDataService.updateUserProfile(userId, profile);
  return profile;
}

/**
 * Reconcile legacy MMR fields with time-control-specific data.
 * Fixes accounts where timeControlRatings shows placement complete
 * but legacy fields (mmr.isPlaced, mmr.placementGames) are stale.
 * Runs on profile load — only saves if changes are needed.
 */
export async function reconcileLegacyFields(
  userId: string,
  profile: UserProfile
): Promise<UserProfile> {
  const tcRatings = profile.mmr.timeControlRatings;
  if (!tcRatings) return profile;

  // Check if any time control is placed
  const anyPlaced = (['marathon', 'standard', 'sprint'] as const).some(
    tc => tcRatings[tc]?.isPlaced
  );

  // Detect stale legacy fields
  const legacyStale =
    (anyPlaced && !profile.mmr.isPlaced) ||
    (!anyPlaced && !profile.mmr.isPlaced && (() => {
      // Check if placementGames is behind the best TC
      const bestGames = Math.max(
        tcRatings.marathon?.placementGames ?? 0,
        tcRatings.standard?.placementGames ?? 0,
        tcRatings.sprint?.placementGames ?? 0
      );
      return bestGames > profile.mmr.placementGames;
    })());

  if (!legacyStale) return profile;

  MMRService.syncLegacyFields(profile);

  await UserDataService.updateUserProfile(userId, profile);
  return profile;
}
